import { checkItemLocaleCode } from "../components/helper";
import { updateValueService } from "../shared/updateValueService";
import {
    RECIPE_INGREDIENT_GROUP_BASE,
    RECIPE_INGREDIENT_GROUP_ADDS,
    RECIPE_INGREDIENT_GROUP_TASTY,
    RECIPE_INGREDIENT_GROUP_LIQUID,
    MIN_PREFIX,
} from "../constant";
import { RECIPES_URL, deleteFetchRequest, getFetchRequest, postFetchRequest } from "../store/requests";
import {errorCallback, makeUrl, successCallbackEmpty} from "../util";
import {RecipeCharacteristicCategory, RecipeTaste} from "./recipeTypes";

const removeElementFromArray = (elements, conditionBeInArray) => {
    const result = [];
    elements.forEach((it) => {
        if (conditionBeInArray(it)) {
            result.push(it);
        }
    });
    return result;
};

const isRecipeCharacteristicActive = (recipe, characteristicCategory, characteristicValue) => {
    if (recipe && recipe.characteristics) {
        return !(recipe.characteristics.find(it => it.value === characteristicValue && it.category === characteristicCategory) === undefined);
    }
    return false;
};

const buildRecipeBase = (recipe) => {
    return {
        recipeType: recipe.recipeType,
        takeAsIs: recipe.takeAsIs,
        baseMin: recipe.baseMin,
        baseMax: recipe.baseMax,
        addsMin: recipe.addsMin,
        addsMax: recipe.addsMax,
        tastyMin: recipe.tastyMin,
        tastyMax: recipe.tastyMax,
        baseShare: recipe.baseShare ?? 70,
        addsShare: recipe.addsShare ?? 30,
        defaultKashaConsistency: recipe.defaultKashaConsistency || null,
    };
};

export const recipeSaveService = {
    currentRecipe: {},
    lastRecipeUpdateTime: null,
    recipeUpdateObservers: [],
    loadRecipe(recipeId, callback) {
        try {
            getFetchRequest(makeUrl([RECIPES_URL, recipeId, '/']), (response) => {
                this.currentRecipe = response;
                getFetchRequest(makeUrl([RECIPES_URL, recipeId, '/ingredient/']), (result) => {
                    this.currentRecipe.ingredient = result;
                    callback();
                    this.notifyRecipeUpdateObservers();
                }, errorCallback);
            }, errorCallback);
        } catch(err) {
            console.log(err);
        }
    },
    getRecipe() {
        return this.currentRecipe;
    },
    getTasteCharacteristics() {
        return Object.values(RecipeTaste).map(it => {
            return {category: RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_TASTE, value: it, code: it, isActive: isRecipeCharacteristicActive(this.currentRecipe, RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_TASTE, it)};
        });
    },
    getCurrentRecipeSource() {
        return this.currentRecipe.characteristics?.find(it => it.category === RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_SOURCE);
    },
    getDateLastUpdate() {
        return this.lastRecipeUpdateTime;
    },
    registerItemUpdateObserver(observer) {
        this.recipeUpdateObservers.push(observer);
    },
    unRegisterItemUpdateObserver(observer) {
        this.recipeUpdateObservers = this.recipeUpdateObservers.filter(productUpdateFunction => productUpdateFunction !== observer);
    },
    notifyRecipeUpdateObservers() {
        this.recipeUpdateObservers.forEach(productUpdateFunction => productUpdateFunction(this.currentRecipe));
    },
    getIngredientGroupMinNumber(groupName) {
        if (RECIPE_INGREDIENT_GROUP_BASE === groupName) {
            return this.currentRecipe.baseMin;
        }
        if (RECIPE_INGREDIENT_GROUP_ADDS === groupName) {
            return this.currentRecipe.addsMin;
        }
        if (RECIPE_INGREDIENT_GROUP_TASTY === groupName) {
            return this.currentRecipe.tastyMin;
        }
        if (RECIPE_INGREDIENT_GROUP_LIQUID === groupName) {
            return 1;
        }
    },
    getIngredientGroupMaxNumber(groupName) {
        if (RECIPE_INGREDIENT_GROUP_BASE === groupName) {
            return this.currentRecipe.baseMax;
        }
        if (RECIPE_INGREDIENT_GROUP_ADDS === groupName) {
            return this.currentRecipe.addsMax;
        }
        if (RECIPE_INGREDIENT_GROUP_TASTY === groupName) {
            return this.currentRecipe.tastyMax;
        }
        if (RECIPE_INGREDIENT_GROUP_LIQUID === groupName) {
            return 1;
        }
    },
    updateIngredientGroupMaxNumber(ingredientsGroup, ingredientsGroupNumber, value) {
        if (RECIPE_INGREDIENT_GROUP_LIQUID === ingredientsGroup) {
            return;
        }
        if (RECIPE_INGREDIENT_GROUP_BASE === ingredientsGroup) {
            if (ingredientsGroupNumber === MIN_PREFIX) {
                this.currentRecipe.baseMin = value;
            } else {
                this.currentRecipe.baseMax = value;
            }
        }
        if (RECIPE_INGREDIENT_GROUP_ADDS === ingredientsGroup) {
            if (ingredientsGroupNumber === MIN_PREFIX) {
                this.currentRecipe.addsMin = value;
            } else {
                this.currentRecipe.addsMax = value;
            }
        }
        if (RECIPE_INGREDIENT_GROUP_TASTY === ingredientsGroup) {
            if (ingredientsGroupNumber === MIN_PREFIX) {
                this.currentRecipe.tastyMin = value;
            } else {
                this.currentRecipe.tastyMax = value;
            }
        }
        postFetchRequest(
            makeUrl([RECIPES_URL, this.currentRecipe.id, '/']),
            buildRecipeBase(this.currentRecipe),
            successCallbackEmpty,
            errorCallback,
            true,
        );
        this.lastRecipeUpdateTime = Date.now();
    },
    updateGroupShare(groupName, value) {
        const parsed = Number(value);
        if (Number.isNaN(parsed)) {
            return;
        }
        if (groupName === RECIPE_INGREDIENT_GROUP_BASE) {
            this.currentRecipe.baseShare = parsed;
        }
        if (groupName === RECIPE_INGREDIENT_GROUP_ADDS) {
            this.currentRecipe.addsShare = parsed;
        }
        postFetchRequest(
            makeUrl([RECIPES_URL, this.currentRecipe.id, '/']),
            buildRecipeBase(this.currentRecipe),
            successCallbackEmpty,
            errorCallback,
            true,
        );
        this.lastRecipeUpdateTime = Date.now();
        this.notifyRecipeUpdateObservers();
    },
    updateIngredientProportion(ingredientId, patch) {
        const currentIngredient = this.currentRecipe.ingredient.find((it) => it.id === ingredientId);
        if (!currentIngredient) {
            return;
        }
        Object.assign(currentIngredient, patch);
        const payload = {
            ingredientType: currentIngredient.ingredientType,
            ingredientGroup: currentIngredient.ingredientGroup,
            unitWeight: currentIngredient.unitWeight ?? 1,
            amountKind: currentIngredient.amountKind || null,
            amountValue: currentIngredient.amountValue ?? null,
            ingredientParts: (currentIngredient.ingredientParts || []).map((part) => ({
                productCode: part.productCode,
                heatTreatment: part.heatTreatment || null,
                ingredientForms: (part.ingredientForms || [])
                    .map((form) => (
                        typeof form === 'string'
                            ? { formCode: form, formType: 'product-part', formGroup: 'any' }
                            : form
                    ))
                    .filter((form) => form.formType !== 'heatTreatment'),
            })),
        };
        postFetchRequest(
            makeUrl([RECIPES_URL, this.currentRecipe.id, '/ingredient/', ingredientId, '/']),
            payload,
            successCallbackEmpty,
            errorCallback,
            true,
        );
        this.lastRecipeUpdateTime = Date.now();
        this.notifyRecipeUpdateObservers();
    },
    updateRecipeName(locale, value) {
        postFetchRequest(makeUrl([RECIPES_URL, this.currentRecipe.id, '/name/']),
            {
                localizedName: value,
                locale: locale,
            },
            () => {
                if(this.currentRecipe.names.length && this.currentRecipe.names.find(it => it.locale === locale)) {
                    this.currentRecipe.names.forEach(it => {
                        if(it.locale === locale) {
                            it.name = value;
                        }
                    });
                } else {
                    this.currentRecipe.names.push({
                        locale: locale,
                        name: value,
                    });
                }
                this.lastRecipeUpdateTime = Date.now();
            }, errorCallback, true);
    },
    updateItemLocalization(locale, value) {
        this.updateRecipeName(locale, value);
    },
    updateSourceCharacteristic(characteristicValue, characteristicCategory) {
        try {
            const currentSources = this.currentRecipe.characteristics.filter(it => it.category === characteristicCategory);
            if (currentSources && currentSources.length > 0) {
                currentSources.forEach(characteristic => {
                    deleteFetchRequest(makeUrl([RECIPES_URL, this.currentRecipe.id, `/characteristic/${characteristic.id}/`]),
                        successCallbackEmpty, errorCallback);
                });
            }
            postFetchRequest(makeUrl([RECIPES_URL, this.currentRecipe.id, '/characteristic/']),
                {
                    category: characteristicCategory,
                    value: characteristicValue,
                },
                (response) => {
                    if(!this.currentRecipe.characteristics.find(it => it.category === characteristicCategory)) {
                        this.currentRecipe.characteristics.push({
                            "category": characteristicCategory,
                            "value": response.value,
                            "id": response.id,
                        });
                    } else {
                        this.currentRecipe.characteristics.forEach(it => {
                            if(it.category === characteristicCategory) {
                                it.value = response.value;
                                it.id = response.id;
                            }
                        });
                    }
                    this.lastRecipeUpdateTime = Date.now();
                    this.notifyRecipeUpdateObservers();
                }, errorCallback);
        } catch(err) {
            console.log(err);
        }
    },
    updateCharacteristic(characteristicValue, characteristicCategory) {
        try {
            const currentCharacteristic = this.currentRecipe.characteristics.find(it => it.value === characteristicValue && it.category === characteristicCategory);
            if (currentCharacteristic === undefined) {
                postFetchRequest(makeUrl([RECIPES_URL, this.currentRecipe.id, '/characteristic/']), {
                    category: characteristicCategory,
                    value: characteristicValue,
                },
                (response) => {
                    this.currentRecipe.characteristics.push(response);
                    this.lastRecipeUpdateTime = Date.now();
                    this.notifyRecipeUpdateObservers();
                }, errorCallback);
            } else {
                deleteFetchRequest(makeUrl([RECIPES_URL, this.currentRecipe.id, `/characteristic/${currentCharacteristic.id}/`]),
                    (response) => {
                        this.currentRecipe.characteristics = removeElementFromArray(this.currentRecipe.characteristics, (it) => {
                            if (it.id === currentCharacteristic.id) return false;
                            return true;
                        });
                        this.lastRecipeUpdateTime = Date.now();
                        this.notifyRecipeUpdateObservers();
                    }, errorCallback);
            }
        } catch(err) {
            console.log(err);
        }
    },
    addIngredient(ingredient) {
        try {
            postFetchRequest(makeUrl([RECIPES_URL, this.currentRecipe.id, '/ingredient/']), ingredient, 
                (response) => {
                    this.currentRecipe.ingredient.push(response);
                    this.lastRecipeUpdateTime = Date.now();
                    this.notifyRecipeUpdateObservers();
                }, errorCallback);
        } catch(err) {
            console.log(err);
        }
    },
    deleteIngredient(ingredientId) {
        try {
            this.currentRecipe.ingredient = this.currentRecipe.ingredient.filter(it => it.id !== ingredientId);
            deleteFetchRequest(makeUrl([RECIPES_URL, this.currentRecipe.id, '/ingredient/', ingredientId, '/']), successCallbackEmpty, errorCallback);
            this.lastRecipeUpdateTime = Date.now();
            this.notifyRecipeUpdateObservers();
        } catch(err) {
            console.log(err);
        }
    },
    deleteIngredientForm(ingredientId, formCode) {
        try {
            const currentIngredient = this.currentRecipe.ingredient.find(it => it.id === ingredientId);
            currentIngredient.ingredientParts[0].ingredientForms = currentIngredient.ingredientParts[0].ingredientForms.filter(it => it !== formCode);
            postFetchRequest(makeUrl([RECIPES_URL, this.currentRecipe.id, '/ingredient/', currentIngredient.id, '/']), currentIngredient, successCallbackEmpty, errorCallback, true);
            this.lastRecipeUpdateTime = Date.now();
            this.notifyRecipeUpdateObservers();
        } catch(err) {
            console.log(err);
        }
    },
    addIngredientForm(ingredientId, form) {
        try {
            const currentIngredient = this.currentRecipe.ingredient.find(it => it.id === ingredientId);
            currentIngredient.ingredientParts[0].ingredientForms.push(form.code);
            updateValueService.updateItemValue(() => {
                postFetchRequest(makeUrl([RECIPES_URL, this.currentRecipe.id, '/ingredient/', ingredientId, '/']), currentIngredient, () => {
                    this.lastRecipeUpdateTime = Date.now();
                    this.notifyRecipeUpdateObservers();
                }, errorCallback, true);
            });
            this.notifyRecipeUpdateObservers();
        } catch(err) {
            console.log(err);
        }
    },
    updateRecipeType(value) {
        this.currentRecipe.recipeType = value;
        postFetchRequest(
            makeUrl([RECIPES_URL, this.currentRecipe.id, '/']),
            buildRecipeBase(this.currentRecipe),
            successCallbackEmpty,
            errorCallback,
        );
        this.lastRecipeUpdateTime = Date.now();
        this.notifyRecipeUpdateObservers();
    },
    updateDefaultKashaConsistency(value) {
        this.currentRecipe.defaultKashaConsistency = value || null;
        postFetchRequest(
            makeUrl([RECIPES_URL, this.currentRecipe.id, '/']),
            buildRecipeBase(this.currentRecipe),
            successCallbackEmpty,
            errorCallback,
            true,
        );
        this.lastRecipeUpdateTime = Date.now();
        this.notifyRecipeUpdateObservers();
    },
    updateTakeAsIs() {
        this.currentRecipe.takeAsIs = !this.currentRecipe.takeAsIs;
        postFetchRequest(
            makeUrl([RECIPES_URL, this.currentRecipe.id, '/']),
            buildRecipeBase(this.currentRecipe),
            successCallbackEmpty,
            errorCallback,
            true,
        );
        this.lastRecipeUpdateTime = Date.now();
        this.notifyRecipeUpdateObservers();
    },
    cleanService() {
        this.currentRecipe = {};
    },
};
